import { resolveArgonRequest } from '../constants/argon';
// src/services/api.js
import config from '../config';
import { resolveChatModelRequest } from '../constants/modelPresets';
import { resolveServiceTierRequest } from '../constants/serviceTier';
import {
    getPublishedImageMetadataUrl,
    getPublishedImageUrl,
    getPublishedImageId,
    normalizeContentBlocks,
} from '../utils/publishedImages';

// Flip: route all authenticated app calls to FastAPI v2 base
// Note: In production, /api_v2 is typically nginx‑proxied to the FastAPI
// /api routes. In dev, CRA proxies these relative paths to whatever is
// running on :8081. Some environments may still route /api to the legacy
// Flask backend, so our logout helper below will best‑effort hit both
// /api_v2/* and /api/* variants to ensure cookies are cleared.
export const API_BASE_URL = config.apiV2Url;
export const API_V2_BASE_URL = config.apiV2Url;
export const MEDIA_API_BASE_URL = config.apiUrl;

const modelMatches = (candidate, selectedModel) => {
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const selected = normalize(selectedModel);
  if (!selected || !candidate || typeof candidate !== 'object') return false;
  return [candidate.id, candidate.model, candidate.model_id, candidate.value, candidate.name]
    .some((value) => normalize(value) === selected);
};

// Enable with: localStorage.setItem('henosis:threadPerf', '1')
// or window.__HENOSIS_THREAD_PERF__ = true.  Diagnostics intentionally
// report sizes/counts/timings only; message contents are never logged.
const threadPerfEnabled = () => {
  try {
    return typeof window !== 'undefined' && (
      window.__HENOSIS_THREAD_PERF__ === true
      || window.localStorage?.getItem('henosis:threadPerf') === '1'
    );
  } catch (_) {
    return false;
  }
};

const threadPerfNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const emitThreadPerf = (diagnostics) => {
  if (!threadPerfEnabled() || !diagnostics) return;
  console.info('[henosis-thread-perf]', diagnostics);
};

const supportsImageInput = (capabilities) => {
  if (!capabilities || typeof capabilities !== 'object') return false;
  const nested = capabilities.capabilities && typeof capabilities.capabilities === 'object'
    ? capabilities.capabilities
    : {};
  const vision = capabilities.vision ?? nested.vision;
  return vision === true
    || vision?.supported === true
    || vision?.available === true
    || capabilities.route_supports_image_input === true
    || nested.route_supports_image_input === true
    || capabilities.supports_vision === true
    || nested.supports_vision === true
    || capabilities.supportsVision === true
    || nested.supportsVision === true
    || capabilities.image_input === true
    || nested.image_input === true
    || capabilities.imageInput === true
    || nested.imageInput === true
    || capabilities.images === true
    || nested.images === true;
};

const getMediaCapabilityValue = (payload, route, selectedModel) => {
  if (!payload || typeof payload !== 'object') return false;
  if (Array.isArray(payload.providers)) {
    const model = payload.providers
      .flatMap((provider) => Array.isArray(provider?.models) ? provider.models : [])
      .find((candidate) => modelMatches(candidate, selectedModel));
    return supportsImageInput(model);
  }
  const routeCapabilities = payload.routes?.[route]
    || payload.capabilities?.[route]
    || payload[route]
    || payload;
  return supportsImageInput(routeCapabilities);
};

export const fetchMediaCapabilities = async (route = 'backend_henosis', selectedModel = '') => {
  const response = await fetch(
    `${MEDIA_API_BASE_URL}/media/capabilities?route=${encodeURIComponent(route)}`,
    { method: 'GET', credentials: 'include' }
  );
  const data = await handleResponse(response);
  return {
    ...((data && typeof data === 'object') ? data : {}),
    supportsVision: getMediaCapabilityValue(data, route, resolveChatModelRequest({ model: selectedModel }, route).model),
  };
};

export const uploadImageAttachment = async (file) => {
  if (!(file instanceof Blob)) throw new Error('An image file is required.');
  const formData = new FormData();
  formData.append('file', file, file.name || 'image');
  const response = await fetch(`${MEDIA_API_BASE_URL}/media/images`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await handleResponse(response);
  const attachment = data?.attachment && typeof data.attachment === 'object' ? data.attachment : data;
  if (!attachment?.attachment_id || !attachment?.url) {
    throw new Error('Image upload did not return an attachment ID and URL.');
  }
  return attachment;
};

export const getPublishedImageMetadata = async (attachmentId, options = {}) => {
  const imageId = getPublishedImageId(attachmentId);
  const endpoint = getPublishedImageMetadataUrl(imageId);
  if (!endpoint) throw new Error('A valid published image ID is required.');
  const response = await fetch(endpoint, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    redirect: 'error',
    headers: { 'Accept': 'application/json' },
    signal: options?.signal,
  });
  const data = await handleResponse(response);
  const metadata = data?.image && typeof data.image === 'object'
    ? data.image
    : (data?.published_image && typeof data.published_image === 'object' ? data.published_image : data);
  if (getPublishedImageId(metadata?.attachment_id || metadata?.id) !== imageId || metadata?.is_owner !== true) {
    throw new Error('Published image metadata failed the owner identity check.');
  }
  return metadata;
};

export const updatePublishedImageVisibility = async (attachmentId, visibility) => {
  const imageId = getPublishedImageId(attachmentId);
  const endpoint = getPublishedImageMetadataUrl(imageId);
  const nextVisibility = String(visibility || '').trim().toLowerCase();
  if (!endpoint) throw new Error('A valid published image ID is required.');
  if (!['private', 'public'].includes(nextVisibility)) {
    throw new Error('Published image visibility must be private or public.');
  }
  const body = { visibility: nextVisibility };
  const response = await fetch(`${endpoint}/visibility`, {
    method: 'PATCH',
    credentials: 'include',
    cache: 'no-store',
    redirect: 'error',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await handleResponse(response);
  const metadata = data?.image && typeof data.image === 'object'
    ? data.image
    : (data?.published_image && typeof data.published_image === 'object' ? data.published_image : data);
  if (getPublishedImageId(metadata?.attachment_id || metadata?.id) !== imageId || metadata?.is_owner !== true) {
    throw new Error('Published image visibility response failed the owner identity check.');
  }
  return metadata;
};

export const fetchPublishedImageBlob = async (block, kind = 'image', visibility = 'private', options = {}) => {
  const imageUrl = getPublishedImageUrl(block, kind);
  if (!imageUrl) throw new Error('A valid published image URL is required.');
  const response = await fetch(imageUrl, {
    method: 'GET',
    credentials: ['public', 'public_download'].includes(kind) ? 'omit' : 'include',
    redirect: 'error',
    headers: { 'Accept': 'image/*' },
    cache: 'no-store',
    signal: options?.signal,
  });
  if (!response.ok) throw new Error(`Image request failed (${response.status})`);
  return response.blob();
};

const normalizeStreamPart = (part) => {
    if (!part || typeof part !== 'object') return part;
    const normalized = { ...part };
    if (normalized.data && typeof normalized.data === 'object') {
        normalized.data = { ...normalized.data };
    }
    const type = String(normalized.type || normalized.part_type || '').trim().toLowerCase();
    if (type === 'tool.call') normalized.type = 'tool_call';
    else if (type === 'tool.result') normalized.type = 'tool_result';
    else if (type === 'thinking.insight' || type === 'thinking.progress' || type === 'thinking.update' || type === 'commentary') normalized.type = 'thinking_insight';
    else if (type && !normalized.type) normalized.type = type;
    return normalized;
};

// Backend adapters may carry provider progress through the existing
// commentary/thinking channel. Progress is public live output even when
// reasoning insights are disabled; signatures and provider-native state never
// belong in the browser transcript.
export const normalizeThinkingInsight = (value) => {
    const source = value && typeof value === 'object' ? { ...value } : { text: String(value || '') };
    const mode = String(source.mode || source.kind || (source.progress === true ? 'progress' : '')).trim().toLowerCase();
    delete source.signature;
    delete source.thinking_signature;
    delete source.redacted_thinking;
    delete source.native_state;
    delete source.provider_state;
    delete source.progress;
    if (mode === 'update') source.mode = 'updates';
    else if (mode === 'commentary') source.mode = 'progress';
    else if (mode === 'progress') source.mode = 'progress';
    return source;
};

export const isProgressThinkingInsight = (value) => {
    const mode = String(value?.mode || value?.kind || '').trim().toLowerCase();
    return ['progress', 'update', 'updates', 'commentary'].includes(mode)
        || String(value?.display || '').trim().toLowerCase() === 'updates'
        || value?.progress === true;
};

const normalizeFunctionLog = (entry) => {
    if (!entry || typeof entry !== 'object') return entry;
    const normalized = { ...entry };
    const type = String(normalized.type || '').trim().toLowerCase();
    const data = normalized.data && typeof normalized.data === 'object' ? { ...normalized.data } : null;
    delete normalized.data;
    if (type === 'tool.call') return { ...normalized, ...(data || {}), type: 'call' };
    if (type === 'tool.result') return { ...normalized, ...(data || {}), type: 'result' };
    if (type === 'call' || type === 'result') return { ...normalized, type };
    return data ? { ...normalized, ...data } : normalized;
};

const deriveFunctionLogsFromStreamParts = (streamParts) => {
    if (!Array.isArray(streamParts)) return [];
    return streamParts
        .map((part) => {
            if (!part || typeof part !== 'object') return null;
            const type = String(part.type || '').trim().toLowerCase();
            const data = part.data && typeof part.data === 'object' ? { ...part.data } : {};
            if (type === 'tool_call') return { ...data, type: 'call' };
            if (type === 'tool_result') return { ...data, type: 'result' };
            return null;
        })
        .filter(Boolean);
};

export const normalizeThreadMessage = (message) => {
    if (!message || typeof message !== 'object') return message;
    const normalized = { ...message };

    if (!Array.isArray(normalized.content_blocks) && Array.isArray(normalized.contentBlocks)) {
        normalized.content_blocks = normalizeContentBlocks(normalized.contentBlocks);
    } else if (Array.isArray(normalized.content_blocks)) {
        normalized.content_blocks = normalizeContentBlocks(normalized.content_blocks);
    }

    // Accept either camelCase (web) or snake_case (backend events/payloads).
    if (normalized.thinkingInsight && typeof normalized.thinkingInsight === 'object') {
        normalized.thinkingInsight = normalizeThinkingInsight(normalized.thinkingInsight);
    } else if (normalized.thinking_insight && typeof normalized.thinking_insight === 'object') {
        normalized.thinkingInsight = normalizeThinkingInsight(normalized.thinking_insight);
    }
    if (!normalized.rawProviderMessages && Array.isArray(normalized.raw_provider_messages)) {
        normalized.rawProviderMessages = normalized.raw_provider_messages;
    }
    const hadStreamParts = Array.isArray(normalized.streamParts)
        || Array.isArray(normalized.stream_parts);
    if (!Array.isArray(normalized.streamParts) && Array.isArray(normalized.stream_parts)) {
        normalized.streamParts = normalized.stream_parts.map(normalizeStreamPart);
    } else if (Array.isArray(normalized.streamParts)) {
        normalized.streamParts = normalized.streamParts.map(normalizeStreamPart);
    }
    const hadFunctionLogs = Array.isArray(normalized.functionLogs)
        || Array.isArray(normalized.function_logs);
    const functionLogSource = Array.isArray(normalized.functionLogs)
        ? normalized.functionLogs
        : (Array.isArray(normalized.function_logs) ? normalized.function_logs : null);
    if (functionLogSource) normalized.functionLogs = functionLogSource.map(normalizeFunctionLog);
    if (!hadFunctionLogs && hadStreamParts && Array.isArray(normalized.streamParts) && normalized.streamParts.length > 0) {
        normalized.functionLogs = deriveFunctionLogsFromStreamParts(normalized.streamParts);
    }
    if (!normalized.approvalRequest && normalized.approval_request && typeof normalized.approval_request === 'object') {
        normalized.approvalRequest = { ...normalized.approval_request };
    }
    if (normalized.serviceTier === undefined && normalized.service_tier !== undefined) {
        normalized.serviceTier = normalized.service_tier;
    }

    // Backward-compatible citations envelope:
    //  - legacy: citations is an array/object
    //  - newer: { citations: [...], thinking_insight: {...} }
    if (typeof normalized.citations === 'string') {
        try {
            normalized.citations = JSON.parse(normalized.citations);
        } catch (_) {}
    }
    if (normalized.citations && typeof normalized.citations === 'object' && !Array.isArray(normalized.citations)) {
        const c = normalized.citations;
        if (Object.prototype.hasOwnProperty.call(c, 'citations')) {
            normalized.citations = c.citations;
        }
        if (!normalized.thinkingInsight && c.thinking_insight && typeof c.thinking_insight === 'object') {
            normalized.thinkingInsight = normalizeThinkingInsight(c.thinking_insight);
        } else if (!normalized.thinkingInsight && c.thinkingInsight && typeof c.thinkingInsight === 'object') {
            normalized.thinkingInsight = normalizeThinkingInsight(c.thinkingInsight);
        }
    }

    return normalized;
};

const markDisplayProjectionMessage = (message, state) => {
    if (!message || typeof message !== 'object' || state?.view !== 'display') return message;
    return {
        ...message,
        // Internal provenance survives all UI-detail overlays. It is removed
        // only after canonical full state has been fetched for persistence.
        __henosis_display_projection: true,
        __henosis_display_version: state.display_version,
    };
};

export const normalizeThreadState = (state, { view } = {}) => {
    if (!state || typeof state !== 'object') return state;
    const resolvedView = view || state.view;
    const normalized = {
        ...state,
        ...(resolvedView ? { view: resolvedView } : {}),
        ...(state.display_version != null ? { display_version: state.display_version } : {}),
    };
    if (Array.isArray(state.messages)) {
        normalized.messages = state.messages
            .map(normalizeThreadMessage)
            .map((message) => markDisplayProjectionMessage(message, normalized));
    }
    if (Array.isArray(state.active_runs)) normalized.active_runs = state.active_runs;
    return normalized;
};

/** * Handles the API response, parsing JSON and handling errors. * Also processes any message field in the JSON response. * * @param {Response} response - The fetch Response object * @returns {Promise<Object|string>} - Parsed data or text content * @throws {Error|Object} - If there's an error in the response or parsing. Throws custom error object if available. */
const handleResponse = async (response, diagnostics = null) => {
    const bodyStart = threadPerfNow();
    const text = await response.text();
    if (diagnostics) {
        diagnostics.bodyReadMs = Number((threadPerfNow() - bodyStart).toFixed(2));
        diagnostics.contentLength = response.headers.get('content-length');
        diagnostics.responseBytes = new TextEncoder().encode(text).byteLength;
        diagnostics.serverTiming = response.headers.get('server-timing');
        diagnostics.backendPerf = response.headers.get('x-thread-perf');
    }
    try {
        const parseStart = threadPerfNow();
        const data = JSON.parse(text);
        if (diagnostics) diagnostics.jsonParseMs = Number((threadPerfNow() - parseStart).toFixed(2));
        if (!response.ok) {
            const detail = typeof data.detail === 'string'
                ? data.detail
                : (data.detail ? JSON.stringify(data.detail) : null);
            // Create an error object that includes all relevant fields from server response
            const errorData = {
                error: data.error || detail || 'An unknown error occurred',
                message: data.message || detail || null, // Use server message/detail if available
                code: data.code || null,
                status: response.status // Include HTTP status code
            };
            // Use a more specific message if available from the server
            const errorMessage = errorData.message || errorData.error || `HTTP error! status: ${response.status}`;
            const error = new Error(errorMessage);
            // Attach additional properties to the error object
            Object.assign(error, errorData);
            throw error; // Throw the enhanced error object
        }
        // Handle message field if present in successful response
        if (data.message) {
            console.log('Message from server:', data.message);
        }
        return data;
    } catch (error) {
        // If it's an enhanced error we created, re-throw it
        if (error.status && (error.error || error.message)) {
            throw error;
        }
        // If parsing failed but response was ok, return text (e.g., non-JSON response)
        if (response.ok) {
            return text;
        }
        // Fallback error for parsing failures on error responses
        const fallbackError = new Error(error.message || `Failed to parse response or unknown error. Status: ${response.status}`);
        fallbackError.status = response.status;
        fallbackError.responseText = text; // Include raw text for debugging
        throw fallbackError;
    }
};

export const createFounderActivationCheckout = async () => {
  const response = await fetch(`${API_V2_BASE_URL}/stripe/create-checkout-session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({}),
  });
  const data = await handleResponse(response);
  const checkoutUrl = data?.url || data?.checkout_url;

  if (typeof checkoutUrl !== 'string' || !checkoutUrl.trim()) {
    throw new Error('Checkout did not return a secure redirect URL. Please try again.');
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(checkoutUrl);
  } catch (_) {
    throw new Error('Checkout returned an invalid redirect URL. Please try again.');
  }

  if (parsedUrl.protocol !== 'https:') {
    throw new Error('Checkout returned an insecure redirect URL. Please try again.');
  }

  return { ...data, url: parsedUrl.toString() };
};

const validateSecureCheckoutUrl = (data) => {
  const checkoutUrl = data?.url || data?.checkout_url;

  if (typeof checkoutUrl !== 'string' || !checkoutUrl.trim()) {
    throw new Error('Checkout did not return a secure redirect URL. Please try again.');
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(checkoutUrl);
  } catch (_) {
    throw new Error('Checkout returned an invalid redirect URL. Please try again.');
  }

  if (parsedUrl.protocol !== 'https:') {
    throw new Error('Checkout returned an insecure redirect URL. Please try again.');
  }

  return parsedUrl.toString();
};

/**
 * Creates a one-time credit top-up checkout session.
 *
 * The server owns the credit price, estimated Stripe fee, payment-method
 * eligibility, and final amount charged. The client only submits the
 * requested USD credit amount and validates the returned Checkout URL.
 */
const newCreditCheckoutIdempotencyKey = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `credit-checkout-${window.crypto.randomUUID()}`;
  }
  return `credit-checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createCreditCheckout = async (credits, { returnPath } = {}) => {
  const amount = Number(credits);
  if (!Number.isFinite(amount) || amount < 5 || amount > 1000) {
    throw new Error('Credit purchases must be between $5 and $1,000 USD.');
  }
  const principalCents = Math.round(amount * 100);
  if (Math.abs((principalCents / 100) - amount) > Number.EPSILON * 100) {
    throw new Error('Credit purchases may have no more than two decimal places.');
  }

  const response = await fetch(`${API_V2_BASE_URL}/stripe/create-credit-checkout-session`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Idempotency-Key': newCreditCheckoutIdempotencyKey(),
    },
    body: JSON.stringify({
      principal_cents: principalCents,
      ...(returnPath ? { return_path: returnPath } : {}),
    }),
  });
  const data = await handleResponse(response);
  return { ...data, url: validateSecureCheckoutUrl(data) };
};

export const getCreditBalance = async () => {
  const response = await fetch(`${API_V2_BASE_URL}/credits/balance`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Accept': 'application/json' },
  });
  return handleResponse(response);
};

export const getCreditPurchases = async () => {
  const response = await fetch(`${API_V2_BASE_URL}/credits/purchases`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Accept': 'application/json' },
  });
  const data = await handleResponse(response);
  return {
    ...((data && typeof data === 'object') ? data : {}),
    purchases: Array.isArray(data?.purchases) ? data.purchases : [],
  };
};

const isProjectedMessage = (message) => !!(
    message
    && typeof message === 'object'
    && (
        message.__henosis_display_projection === true
        || message.details_loaded === false
        || (message.detail_ref && typeof message.detail_ref === 'object')
    )
);

export const containsDisplayProjection = (messages) => (
    Array.isArray(messages) && messages.some(isProjectedMessage)
);

const getPersistedMessageId = (message) => {
    const value = message?.message_id ?? message?.id ?? message?.detail_ref?.message_id;
    return value == null || value === '' ? null : String(value);
};

const mergeProjectedMessagesWithFullState = (requestedMessages, fullMessages) => {
    const canonicalById = new Map(
        (Array.isArray(fullMessages) ? fullMessages : [])
            .map((message) => [getPersistedMessageId(message), message])
            .filter(([id]) => id != null)
    );
    const hiddenFields = [
        'functionLogs',
        'function_logs',
        'streamParts',
        'stream_parts',
        'rawProviderMessages',
        'raw_provider_messages',
        'conversationStateV2',
        'conversation_state_v2',
        'providerReplay',
        'provider_replay',
    ];

    return requestedMessages.map((message) => {
        if (!isProjectedMessage(message)) return message;
        const messageId = getPersistedMessageId(message);
        const canonical = messageId ? canonicalById.get(messageId) : null;
        if (!canonical) {
            throw new Error('Cannot safely persist a projected message without a matching full-state message.');
        }
        // Caller-owned visible fields win (including intentional content
        // edits), while hidden fields always come from canonical full state.
        const merged = { ...canonical, ...message };
        hiddenFields.forEach((field) => {
            if (Object.prototype.hasOwnProperty.call(canonical, field)) merged[field] = canonical[field];
            else delete merged[field];
        });
        delete merged.__henosis_display_projection;
        delete merged.__henosis_display_version;
        delete merged.detail_ref;
        delete merged.details_loaded;
        return merged;
    });
};

/** * Saves a conversation to the server. * Handles both new and existing threads with proper validation. * * @param {string} threadName - The name of the thread * @param {Array} messages - The messages in the conversation * @param {string|null} threadUid - The UUID of the thread (null for new threads) * @param {string} selectedModel - The selected AI model * @param {Object} shareSettings - Optional sharing configuration * @returns {Promise} - The server response with thread_uid */
export const saveConversation = async (threadName, messages, threadUid = null, selectedModel, shareSettings = null) => {
    let messagesForSave = Array.isArray(messages) ? messages : [];
    if (containsDisplayProjection(messagesForSave)) {
        if (!threadUid || threadUid.startsWith('temp-')) {
            throw new Error('Cannot persist a display projection without its source thread.');
        }
        const fullState = await getFullThreadState(threadUid);
        if (!fullState || fullState.state_missing || fullState.view === 'display' || !Array.isArray(fullState.messages)) {
            throw new Error('Could not load canonical full thread state before saving.');
        }
        messagesForSave = mergeProjectedMessagesWithFullState(messagesForSave, fullState.messages);
    }
    const isNewThread = !threadUid || threadUid.startsWith('temp-');
    const payload = {
        thread_name: threadName || 'New Chat',
        messages: messagesForSave,
        paste_counter: messagesForSave.pastes?.length || 0,
        selected_model: selectedModel || null,
        is_new_thread: isNewThread, // Correctly determine if it's a new thread
    };

    // Add thread identifier if exists and is not temporary
    if (threadUid && !threadUid.startsWith('temp-')) {
        payload.thread_uid = threadUid;
    }

    // Add share settings if provided
    if (shareSettings) {
        payload.share_settings = shareSettings;
    }

    const response = await fetch(`${API_V2_BASE_URL}/save_conversation`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });
    return handleResponse(response);
};

/** * Generates a concise chat title based on the initial query. * Includes enhanced error handling, logging, and fallback behavior. * * @param {string} initialQuery - The initial user query. * @returns {Promise<string>} - A chat title (max 5 words) or fallback title. */
export const generateChatTitle = async (initialQuery, assistantResponse = '') => {
    const systemPrompt = `Your job is to create a short and safe chat title for the thread based on the first completed exchange.    Requirements:    - Maximum 5 words    - Avoid any potentially offensive or inappropriate language    - Replace inappropriate content with positive alternatives    - Make it friendly and welcoming    - Keep it concise and clear    - Focus on the main topic    - Don't include sensitive information    Example good titles:    - "Code Review Discussion"    - "Python Learning Help"    - "Website Design Planning"    Example bad titles:    - "Complex Discussion About Multiple Topics" (too long)    - "????" (too vague)    - "Query About Something" (not specific enough)    Generate a title for this exchange.`;
    const exchangeText = assistantResponse
        ? `User:\n${initialQuery}\n\nAssistant:\n${assistantResponse}`
        : initialQuery;
    const messagesForTitle = [
        { role: 'user', content: `${systemPrompt}\n\n${exchangeText}` },
    ];
    try {
        // Use the non-streaming chat adapter because it explicitly supports
        // `is_title_generation` and routes title requests through the dedicated
        // low-cost backend title model path.
        const response = await fetch(`${API_V2_BASE_URL}/chat`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                messages: messagesForTitle,
                model: 'deepseek-flash',
                is_title_generation: true,
            }),
        });
        const data = await handleResponse(response);
        const titleText =
            (typeof data === 'object' && data !== null && (
                data.text
                || data.choices?.[0]?.message?.content
                || data.choices?.[0]?.text
            ))
            || '';
        let title = String(titleText || '').trim() || 'New Chat';
        const cleanTitle = title.replace(/[^\w\s-]/g, '').trim();
        const words = cleanTitle.split(/\s+/);
        return words.length > 5 ? words.slice(0, 5).join(' ') : cleanTitle || 'New Chat';
    } catch (error) {
        console.error('generateChatTitle: Error generating title:', error);
        return 'New Chat';
    }
};

export const fetchUsername = async () => {
  try {
    const response = await fetch(`${API_V2_BASE_URL}/username`, {
        method: 'GET',
        credentials: 'include',
      headers: {
         'Content-Type': 'application/json',
       },
    });
    return await handleResponse(response); // Return JSON response
  } catch (error) {
    console.error('API: Error fetching username:', error);
      throw error; // Re-throw to be handled by caller
  }
};

export const getConversations = async (loadType, params = {}, options = {}) => {
  const controller = new AbortController();
  const timeoutDuration = options.timeout || 900000; // 15 minutes default timeout
  const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
  Date.now();
  Math.random().toString(36).substring(7);
  try {
      const queryParams = new URLSearchParams({
          ...(loadType && { type: loadType }),
          ...(params.offset && { offset: params.offset }),
          ...(params.limit && { limit: params.limit }),
          ...(params.thread_uid && { thread_uid: params.thread_uid }),
          include_shared: params.includeShared ?? true // Default to true if not specified
      });
      const endpoint = `${API_V2_BASE_URL}/get_conversations?${queryParams}`;
      const response = await fetch(endpoint, {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal, // Link request to AbortController
          headers: {
              'Content-Type': 'application/json',
          },
      });
      if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, error: ${errorText}`);
      }
      const data = await response.json();

      if (loadType === 'thread' && params.thread_uid) {
          const messages = Array.isArray(data) ? data :
                          data.messages ? data.messages :
                         data.thread?.messages ? data.thread.messages :
                         [];
          return messages.map(normalizeThreadMessage);
      }
      if (data.threads || Array.isArray(data)) {
          const threads = Array.isArray(data) ? data : data.threads || [];
          return threads;
      }
      return [];
  } catch (error) {
      if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeoutDuration/1000} seconds`);
      }
      throw error;
  } finally {
      clearTimeout(timeoutId);
  }
};

const searchQueryCodePointLength = (value) => Array.from(String(value || '')).length;

export const searchThreads = async (query, { limit = 20, offset = 0, signal } = {}) => {
  const trimmedQuery = String(query || '').trim();
  const queryLength = searchQueryCodePointLength(trimmedQuery);
  if (queryLength > 0 && queryLength < 2) {
    throw new Error('Search query must contain at least 2 characters.');
  }
  if (queryLength > 200) {
    throw new Error('Search query must contain 200 characters or fewer.');
  }

  const pageLimit = Number(limit);
  const pageOffset = Number(offset);
  if (!Number.isInteger(pageLimit) || pageLimit < 1 || pageLimit > 50) {
    throw new Error('Search limit must be between 1 and 50.');
  }
  if (!Number.isInteger(pageOffset) || pageOffset < 0 || pageOffset > 100000) {
    throw new Error('Search offset must be between 0 and 100000.');
  }

  const queryParams = new URLSearchParams({
    q: trimmedQuery,
    limit: String(pageLimit),
    offset: String(pageOffset),
  });
  const response = await fetch(`${API_V2_BASE_URL}/threads/search?${queryParams.toString()}`, {
    method: 'GET',
    credentials: 'include',
    signal,
    headers: { Accept: 'application/json' },
  });
  return handleResponse(response);
};

export const getThreadState = async (threadUid, options = {}) => {
  if (!threadUid) throw new Error('threadUid is required');
  const view = options?.view === 'display' ? 'display' : null;
  const requestId = `thread-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const diagnostics = threadPerfEnabled() ? {
    endpoint: 'thread-state', threadUid, requestId, startedAt: new Date().toISOString(),
  } : null;
  const requestStart = threadPerfNow();
  let data;
  try {
    const query = view ? `?view=${encodeURIComponent(view)}` : '';
    const response = await fetch(`${API_V2_BASE_URL}/threads/${encodeURIComponent(threadUid)}/state${query}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Thread-Perf-Request-Id': requestId,
      },
    });
    if (diagnostics) diagnostics.fetchMs = Number((threadPerfNow() - requestStart).toFixed(2));
    data = await handleResponse(response, diagnostics);
  } catch (error) {
    // Some local/prod data sets still contain conversation-list entries whose
    // v2 thread-state row has not been materialized.  Falling back to the
    // older conversation fetch keeps the UI usable instead of surfacing a
    // noisy generic API_ERROR popup on login/thread selection.
    if (error?.status === 404 && view === 'display' && options?.allowDisplayFallback !== false) {
      // Older gateways may not know the additive display view yet. Prefer the
      // canonical full endpoint as a compatibility fallback; never mark that
      // response as a projection or let it enter the partial-save path.
      return getThreadState(threadUid, { allowDisplayFallback: false });
    }
    if (error?.status === 404) {
      const messages = await getConversations('thread', { thread_uid: threadUid }).catch(() => []);
      return normalizeThreadState({
        thread_uid: threadUid,
        messages: Array.isArray(messages) ? messages.map(normalizeThreadMessage) : [],
        active_runs: [],
        thread_revision: null,
        state_missing: true,
      }, { view });
    }
    throw error;
  }
  const normalizeStart = threadPerfNow();
  const result = normalizeThreadState(data, { view });
  result.messages = Array.isArray(result?.messages) ? result.messages : [];
  result.active_runs = Array.isArray(result?.active_runs) ? result.active_runs : [];
  if (diagnostics) {
    diagnostics.normalizeMs = Number((threadPerfNow() - normalizeStart).toFixed(2));
    diagnostics.messageCount = result.messages.length;
    diagnostics.totalMs = Number((threadPerfNow() - requestStart).toFixed(2));
    emitThreadPerf(diagnostics);
  }
  return result;
};

export const getThreadStateDisplay = async (threadUid, options = {}) => (
  getThreadState(threadUid, { ...options, view: 'display' })
);

export const getFullThreadState = async (threadUid, options = {}) => (
  getThreadState(threadUid, { ...options, view: null, allowDisplayFallback: false })
);

export const getMessageDetails = async (threadUid, messageId, revision, options = {}) => {
  if (!threadUid) throw new Error('threadUid is required');
  if (!messageId) throw new Error('messageId is required');
  if (typeof revision !== 'string' || !revision) throw new Error('revision is required');
  const query = new URLSearchParams({ revision });
  const response = await fetch(
    `${API_V2_BASE_URL}/threads/${encodeURIComponent(threadUid)}/messages/${encodeURIComponent(messageId)}/details?${query.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      signal: options?.signal,
      headers: {
        'Accept': 'application/json',
      },
    }
  );
  return handleResponse(response);
};

export const createChatRun = async (payload) => {
  const response = await fetch(`${API_V2_BASE_URL}/chat/runs`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(resolveArgonRequest(resolveServiceTierRequest(resolveChatModelRequest(payload)))),
  });
  const data = await handleResponse(response);
  return {
    ...data,
    thread_state: data?.thread_state
      ? {
          ...data.thread_state,
          messages: Array.isArray(data.thread_state.messages)
            ? data.thread_state.messages.map(normalizeThreadMessage)
            : [],
          active_runs: Array.isArray(data.thread_state.active_runs) ? data.thread_state.active_runs : [],
        }
      : null,
  };
};

export const getChatRun = async (runId) => {
  if (!runId) throw new Error('runId is required');
  const response = await fetch(`${API_V2_BASE_URL}/chat/runs/${runId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return handleResponse(response);
};

export const cancelChatRun = async (runId, reason = null) => {
  if (!runId) throw new Error('runId is required');
  const response = await fetch(`${API_V2_BASE_URL}/chat/runs/${runId}/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ reason: reason || undefined }),
  });
  return handleResponse(response);
};

export const updateThreadMessage = async (threadUid, messageId, content, editMode = 'truncate_from_here') => {
  if (!threadUid) throw new Error('threadUid is required');
  if (!messageId) throw new Error('messageId is required');
  const response = await fetch(`${API_V2_BASE_URL}/threads/${threadUid}/messages/${messageId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ content, edit_mode: editMode }),
  });
  const data = await handleResponse(response);
  return {
    ...data,
    messages: Array.isArray(data?.messages) ? data.messages.map(normalizeThreadMessage) : [],
    active_runs: Array.isArray(data?.active_runs) ? data.active_runs : [],
  };
};

export const retryThreadMessage = async (threadUid, messageId, payload = {}) => {
  if (!threadUid) throw new Error('threadUid is required');
  if (!messageId) throw new Error('messageId is required');
  const response = await fetch(`${API_V2_BASE_URL}/threads/${threadUid}/messages/${messageId}/retry`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(resolveArgonRequest(resolveServiceTierRequest(resolveChatModelRequest(payload)))),
  });
  const data = await handleResponse(response);
  return {
    ...data,
    thread_state: data?.thread_state
      ? {
          ...data.thread_state,
          messages: Array.isArray(data.thread_state.messages)
            ? data.thread_state.messages.map(normalizeThreadMessage)
            : [],
          active_runs: Array.isArray(data.thread_state.active_runs) ? data.thread_state.active_runs : [],
        }
      : null,
  };
};

export const forkThreadAtMessage = async (threadUid, throughMessageId, name = null) => {
  if (!threadUid) throw new Error('threadUid is required');
  if (!throughMessageId) throw new Error('throughMessageId is required');
  const response = await fetch(`${API_V2_BASE_URL}/threads/${threadUid}/fork`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ through_message_id: throughMessageId, name: name || undefined }),
  });
  const data = await handleResponse(response);
  return {
    ...data,
    messages: Array.isArray(data?.messages) ? data.messages.map(normalizeThreadMessage) : [],
    active_runs: Array.isArray(data?.active_runs) ? data.active_runs : [],
  };
};

export const checkAuth = async () => {
  const response = await fetch(`${API_V2_BASE_URL}/check-auth`, {
    method: 'GET', // Use GET for checking auth status typically
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  // Return parsed body so callers can use fields like {authenticated, user}
  return await handleResponse(response); // Use handleResponse to check for errors
};

export const revokeTrustedDevice = async (deviceId) => {
  if (!deviceId) throw new Error('deviceId is required');
  const response = await fetch(`${API_V2_BASE_URL}/revoke-device`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ device_id: deviceId }),
  });
  return handleResponse(response);
};

export const deleteThread = async (threadUid) => {
  const response = await fetch(`${API_V2_BASE_URL}/delete_conversation/${threadUid}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
          'Content-Type': 'application/json',
      },
  });
  return handleResponse(response);
};

export const fetchCustomInstructions = async () => {
  const response = await fetch(`${API_V2_BASE_URL}/get_custom_instructions`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
     },
  });
  const data = await handleResponse(response);
  const experimentalFeatures = typeof data.experimental_features === 'object' && data.experimental_features !== null
      ? data.experimental_features
      // Defaults: web search ON unless the user explicitly turns it off in settings.
      : { memory: false, web_access: true, image_generation: false, anthropic_speed: 'standard', glm_thinking_enabled: true, glm_preserve_thinking: false, thinking_insights: true, local_auth_fallback_policy: 'ask', context_near_full_pct: null, context_handoff_tokens: null };
  return {
    ...data,
    experimental_features: experimentalFeatures,
    sidebar_position: data.sidebar_position || 'left'
   };
};
// src/services/api.js
export const saveCustomInstructions = async (instructions, lastModel, experimentalFeatures, sidebarPosition) => {
  if (sidebarPosition !== 'left' && sidebarPosition !== 'right') {
     throw new Error('Invalid sidebar position. Must be either "left" or "right"');
  }
  if (typeof experimentalFeatures !== 'object' || experimentalFeatures === null) {
    // Defaults: web search ON unless explicitly disabled by the user.
    experimentalFeatures = { memory: false, web_access: true, image_generation: false, anthropic_speed: 'standard', glm_thinking_enabled: true, glm_preserve_thinking: false, thinking_insights: true, local_auth_fallback_policy: 'ask', context_near_full_pct: null, context_handoff_tokens: null };
  }
  // --- FIX: Ensure the keys sent to the backend are correct (snake_case) ---
  const payload = {
    instructions: instructions?.trim() || '',
    last_model: lastModel,
    experimental_features: {
        memory: experimentalFeatures.memory ?? false,
        // Default ON unless explicitly disabled.
        web_access: experimentalFeatures.web_access ?? true, // Corrected from webAccess
        image_generation: experimentalFeatures.image_generation ?? false, // Corrected from imageGeneration
        anthropic_speed: ['standard', 'fast'].includes(experimentalFeatures.anthropic_speed) ? experimentalFeatures.anthropic_speed : 'standard',
        glm_thinking_enabled: experimentalFeatures.glm_thinking_enabled !== false,
        glm_preserve_thinking: !!experimentalFeatures.glm_preserve_thinking,
        // Web UI default is ON; CLI remains independent/off by default.
        thinking_insights: experimentalFeatures.thinking_insights ?? true,
        anthropic_thinking_display: ['updates', 'summarized', 'omitted'].includes(experimentalFeatures.anthropic_thinking_display)
            ? experimentalFeatures.anthropic_thinking_display
            : undefined,
        local_auth_fallback_policy: experimentalFeatures.local_auth_fallback_policy || 'ask',
        context_near_full_pct: experimentalFeatures.context_near_full_pct ?? null,
        context_handoff_tokens: experimentalFeatures.context_handoff_tokens ?? null,
    },
    sidebar_position: sidebarPosition,
  };
  const response = await fetch(`${API_V2_BASE_URL}/save_custom_instructions`, {
      method: 'POST',
      credentials: 'include',
      headers: {
         'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
   });
  return await handleResponse(response);
};

// Dashboard reads only: bound both the fetch and response-body read. Racing
// settlement also protects the UI if a transport ignores AbortSignal.
const dashboardRead = async (path, { signal } = {}) => {
  const controller = new AbortController();
  let timeoutId;
  let onAbort;
  const cancelled = new Promise((_, reject) => {
    onAbort = () => {
      const error = new Error('Dashboard request cancelled.');
      error.name = 'AbortError';
      controller.abort();
      reject(error);
    };
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort, { once: true });
    timeoutId = setTimeout(() => {
      const error = new Error('Usage data took too long to load. Please try again.');
      error.name = 'TimeoutError';
      controller.abort();
      reject(error);
    }, 30_000);
  });
  try {
    return await Promise.race([
      cancelled,
      (async () => {
        if (controller.signal.aborted) return;
        const response = await fetch(`${API_V2_BASE_URL}${path}`, {
          method: 'GET', credentials: 'include',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        return handleResponse(response);
      })(),
    ]);
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', onAbort);
  }
};

export const getDashboardData = (options = {}) => dashboardRead('/dashboard_data', options);

export const getStatsData = async ({ days = 30, year, models_limit, tz } = {}, options = {}) => {
  const query = new URLSearchParams({ days: String(days) });
  if (year) query.set('year', String(year));
  // Preserve zero: the backend contract uses models_limit=0 for unbounded
  // chart model data. `if (models_limit)` silently dropped that request.
  if (models_limit !== undefined && models_limit !== null) query.set('models_limit', String(models_limit));
  if (tz) query.set('tz', String(tz));
  return dashboardRead(`/stats/cli?${query.toString()}`, options);
};

export const getChartPreferences = (options = {}) => dashboardRead('/stats/chart-preferences', options);

export const updateChartPreferences = async (timezone) => {
  if (timezone !== null && (typeof timezone !== 'string' || !timezone.trim())) {
    throw new Error('Timezone must be an IANA time zone or null.');
  }
  const response = await fetch(`${API_V2_BASE_URL}/stats/chart-preferences`, {
    method: 'PUT', credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ timezone }),
  });
  return handleResponse(response);
};

export const searchModelStats = async (searchQuery, { limit = 10 } = {}) => {
  const query = new URLSearchParams({
    q: String(searchQuery || '').trim(),
    limit: String(limit),
  });
  const response = await fetch(`${API_V2_BASE_URL}/stats/models/search?${query.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return handleResponse(response);
};

export const updateThreadName = async (threadId, newName) => {
    const response = await fetch(`${API_V2_BASE_URL}/update_thread_name/${threadId}`, {
       method: 'PUT',
       credentials: 'include',
       headers: {
          'Content-Type': 'application/json',
         },
      body: JSON.stringify({ name: newName }),
    });
    return handleResponse(response);
};

export const shareThread = async (threadUid, shareSettings) => {
  try {
    const payload = shareSettings || {
      access_type: 'public',
      allowed_users: ['ANY'],
      permissions: ['share']
    };
    const response = await fetch(`${API_V2_BASE_URL}/share_thread/${threadUid}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    const data = await handleResponse(response);
    return {
      shareUrl: data.share_url,
      publicId: data.public_id,
      success: data.success ?? (response.ok)
    };
  } catch (error) {
    console.error('API: Error sharing thread:', error);
    throw error;
  }
};

export const unshareThread = async (threadUid) => {
  try {
      const response = await fetch(`${API_V2_BASE_URL}/unshare_thread/${threadUid}`, {
          method: 'POST',
          credentials: 'include',
          headers: {
              'Content-Type': 'application/json',
          }
      });
      return handleResponse(response);
  } catch (error) {
      console.error('API: Error unsharing thread:', error);
      throw error;
  }
};

export const getSharedThread = async (publicId) => {
    try {
       const response = await fetch(`${API_V2_BASE_URL}/shared/${publicId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
             }
        });
        const data = await handleResponse(response);
        if (data && Array.isArray(data.messages)) {
            return {
                ...data,
                messages: data.messages.map(normalizeThreadMessage),
            };
        }
        return data;
    } catch (error) {
       console.error('API: Error fetching shared thread:', error);
       throw error;
   }
}
/**
 * Logs the user out from the FastAPI backend.
 *
 * Behavior:
 *  - Best‑effort revoke of the current device via /api/revoke-device
 *    (through the /api_v2 proxy).
 *  - Best‑effort logout via /api/logout (tries both /api_v2/logout and
 *    /api/logout to handle mixed nginx / legacy setups).
 *
 * Any network/API errors are logged but do not throw, so callers can
 * safely clear client-side auth state regardless of server behavior.
 */
export const fetchLogout = async () => {
  const deviceId = typeof window !== 'undefined'
    ? localStorage.getItem('deviceId')
    : null;

  // 1) Best‑effort: revoke this device so its refresh token can no longer
  //    be used for silent re-auth.
  if (deviceId) {
    try {
      await fetch(`${API_V2_BASE_URL}/revoke-device`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ device_id: deviceId }),
      });
    } catch (err) {
      console.error('API: revoke-device during logout failed (non-fatal):', err);
    }
  }

  // 2) Best‑effort: call FastAPI logout to clear auth cookies.
  // Prefer the /api_v2 proxy, but also try the plain /api path in case
  // this environment routes FastAPI directly on /api.
  const logoutEndpoints = [
    `${API_V2_BASE_URL}/logout`,
    // Avoid duplicate when apiUrl === apiV2Url
    config.apiUrl !== API_V2_BASE_URL ? `${config.apiUrl}/logout` : null,
  ].filter(Boolean);

  for (const url of logoutEndpoints) {
    try {
      await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      // We intentionally ignore response.ok here: even if one variant fails,
      // we still proceed to clear client-side state.
    } catch (err) {
      console.error(`API: logout request to ${url} failed (non-fatal):`, err);
    }
  }
};

export const fetchUserProfile = async () => {
    try {
        const response = await fetch(`${API_V2_BASE_URL}/profile`, {
            method: 'GET',
            credentials: 'include'
        });
        return await handleResponse(response);
    } catch (error) {
        console.error('Fetch Profile Error: ', error)
        throw error;
    }
}

// Stream chat over SSE from authenticated app endpoint
// Yields objects of shape:
//  - { type: 'delta', text }
//  - { type: 'thinking_insight', insight }
//  - { type: 'usage_update', usage, model, provider_step }
//  - { type: 'completed', usage, model, web_search, thinking_insight, raw_provider_messages }
//  - { type: 'error', message }
export async function* streamChat({ messages, model, thread_uid, raw_provider_messages, experimental_features, enable_web_search, enable_tools, tool_mode, enable_thinking_insights, anthropic_thinking_display, context_near_full_pct, context_handoff_tokens, service_tier, anthropic_speed, openai_reasoning_mode, reasoning_effort, openai_reasoning_context, gemini_thinking_level, gemini_max_output_tokens, route = 'backend_henosis', signal, endpoint }) {
  // Preserve structured multimodal block arrays. Stringifying them turns an
  // image attachment into literal JSON text and silently makes follow-up image
  // turns text-only.
  const toSend = Array.isArray(messages)
    ? messages
        .filter(m => !!m && typeof m === 'object' && m.content !== undefined)
        .filter(m => ['user', 'assistant', 'system'].includes(m.role))
        .map(m => ({
          role: m.role,
          content: typeof m.content === 'string'
            ? m.content
            : (Array.isArray(m.content)
                ? m.content.map((block) => (block && typeof block === 'object' ? { ...block } : block))
                : String(m.content ?? '')),
        }))
        .filter(m => typeof m.content === 'string' || Array.isArray(m.content))
    : [];

  const url = endpoint || `${API_V2_BASE_URL}/chat/stream`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify(resolveArgonRequest(resolveServiceTierRequest(resolveChatModelRequest({
      messages: toSend,
      model,
      openai_reasoning_mode,
      reasoning_effort,
      gemini_thinking_level,
      gemini_max_output_tokens,
      openai_reasoning_context,
      thread_uid,
      raw_provider_messages,
      experimental_features,
      // v2 streaming adapter understands these flags; this keeps web search on while file tools are off by default
      enable_web_search: enable_web_search === undefined ? true : !!enable_web_search,
      enable_tools: enable_tools === undefined ? false : !!enable_tools,
      tool_mode: tool_mode || 'AUTO',
      // Web UI defaults ON for optional provider thinking insight.
      enable_thinking_insights: enable_thinking_insights === undefined ? true : !!enable_thinking_insights,
      anthropic_thinking_display: String(model || '').trim().toLowerCase() === 'claude-opus-5-5'
        ? (anthropic_thinking_display || experimental_features?.anthropic_thinking_display || 'updates')
        : undefined,
      glm_thinking_enabled: experimental_features?.glm_thinking_enabled !== false,
      glm_preserve_thinking: !!experimental_features?.glm_preserve_thinking,
      tool_stream: true,
      context_near_full_pct: context_near_full_pct,
      context_handoff_tokens: context_handoff_tokens,
      service_tier: (typeof service_tier === 'string' && service_tier.trim()) ? service_tier.trim().toLowerCase() : undefined,
      anthropic_speed: (typeof anthropic_speed === 'string' && anthropic_speed.trim()) ? anthropic_speed.trim().toLowerCase() : undefined,
    }, route)))),
    signal,
  });

  if (!res.ok || !res.body) {
    let detail = '';
    try {
      const txt = await res.text();
      try {
        const j = JSON.parse(txt);
        detail = j?.detail || j?.error || j?.message || txt;
      } catch {
        detail = txt;
      }
    } catch {}
    const status = `${res.status || ''} ${res.statusText || ''}`.trim();
    throw new Error(`Stream failed${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Robust SSE parsing that tolerates CRLF and multiple data: lines
  const flushEvent = (rawEvent) => {
    if (!rawEvent) return;
    const lines = rawEvent.split(/\r?\n/);
    let eventType = '';
    const dataLines = [];
    for (const line of lines) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    const dataStr = dataLines.join('\n');
    if (!dataStr) return;
    let data;
    try { data = JSON.parse(dataStr); } catch { data = dataStr; }
    const payloadEventType = (data && typeof data === 'object' && typeof data.event === 'string')
      ? data.event.trim()
      : '';
    const logicalEventType = eventType && eventType !== 'message'
      ? eventType
      : (payloadEventType || eventType);
    if (logicalEventType === 'message.delta') {
      const text =
        data?.text ??
        data?.delta ??
        data?.content ??
        (data?.choices && data?.choices[0]?.delta?.content) ?? '';
      if (text) {
        return { type: 'delta', text };
      }
    } else if (['thinking.insight', 'thinking.progress', 'thinking.update', 'commentary'].includes(logicalEventType)) {
      return { type: 'thinking_insight', insight: normalizeThinkingInsight(logicalEventType === 'commentary' ? { ...data, mode: 'progress' } : data) };
    } else if (logicalEventType === 'usage.update') {
      return {
        type: 'usage_update',
        usage: data?.usage,
        model: data?.model,
        provider_step: data?.provider_step,
      };
    } else if (logicalEventType === 'context.handoff.started' || logicalEventType === 'context.handoff.completed') {
      return {
        type: 'context_handoff',
        phase: logicalEventType.endsWith('.started') ? 'started' : 'completed',
        data: (typeof data === 'object' && data !== null) ? data : {},
      };
    } else if (logicalEventType === 'tool.call' || logicalEventType === 'tool.result') {
      return {
        type: logicalEventType === 'tool.call' ? 'tool_call' : 'tool_result',
        data: (typeof data === 'object' && data !== null) ? data : {},
      };
    } else if (logicalEventType === 'message.completed') {
      return {
        type: 'completed',
        usage: data?.usage,
        model: data?.model,
        text: data?.text ?? data?.output_text ?? data?.assistant_text,
        web_search: data?.web_search,
        thinking_insight: data?.thinking_insight ? normalizeThinkingInsight(data.thinking_insight) : undefined,
        raw_provider_messages: data?.raw_provider_messages,
        content_blocks: Array.isArray(data?.content_blocks)
          ? normalizeContentBlocks(data.content_blocks)
          : (Array.isArray(data?.assistant_content_blocks)
            ? normalizeContentBlocks(data.assistant_content_blocks)
            : (Array.isArray(data?.contentBlocks) ? normalizeContentBlocks(data.contentBlocks) : undefined)),
        service_tier: data?.service_tier,
        status: data?.status,
        reason_code: data?.reason_code,
        retry_count: data?.retry_count,
        partial_saved: data?.partial_saved,
        can_continue: data?.can_continue,
        notice: data?.notice,
      };
    } else if (logicalEventType === 'error') {
      return { type: 'error', message: data?.message || (typeof data === 'string' ? data : 'Unknown error') };
    } else {
      // Fallback: treat as delta when no explicit event
      const text = (typeof data === 'string') ? data : (
        data?.text ??
        data?.delta ??
        data?.content ??
        (data?.choices && data?.choices[0]?.delta?.content) ??
        ''
      );
      if (text) {
        return { type: 'delta', text };
      }
    }
    return undefined;
  };

  const findNextSeparator = (buf) => {
    const crlf = '\r\n\r\n';
    const lf = '\n\n';
    const iCRLF = buf.indexOf(crlf);
    const iLF = buf.indexOf(lf);
    if (iCRLF === -1 && iLF === -1) return { idx: -1, len: 0 };
    if (iCRLF === -1) return { idx: iLF, len: lf.length };
    if (iLF === -1) return { idx: iCRLF, len: crlf.length };
    return (iCRLF < iLF) ? { idx: iCRLF, len: crlf.length } : { idx: iLF, len: lf.length };
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    buffer += chunk;
    while (true) {
      const { idx, len } = findNextSeparator(buffer);
      if (idx < 0) break;
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + len);
      const parsed = flushEvent(rawEvent);
      if (parsed) yield parsed;
    }
  }

  // Final flush in case the stream closed without a trailing blank line
  const tail = buffer.trim();
  if (tail) {
    const parsed = flushEvent(tail);
    if (parsed) yield parsed;
  }
}

export async function* streamRun({ runId, afterSeq = 0, signal }) {
  if (!runId) {
    throw new Error('runId is required');
  }
  const url = `${API_V2_BASE_URL}/chat/runs/${encodeURIComponent(runId)}/stream?after_seq=${encodeURIComponent(Number(afterSeq) || 0)}`;
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Accept': 'text/event-stream',
    },
    signal,
  });
  if (!res.ok || !res.body) {
    let detail = '';
    try {
      detail = await res.text();
    } catch (_) {}
    const status = `${res.status || ''} ${res.statusText || ''}`.trim();
    throw new Error(`Run stream failed${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const parseEvent = (rawEvent) => {
    if (!rawEvent) return undefined;
    const lines = rawEvent.split(/\r?\n/);
    let eventType = 'message';
    let eventId = null;
    const dataLines = [];
    for (const line of lines) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim() || 'message';
      else if (line.startsWith('id:')) eventId = Number(line.slice(3).trim()) || null;
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }
    if (dataLines.length === 0) return undefined;
    let data;
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      data = { message: dataLines.join('\n') };
    }
    const base = {
      seq: eventId ?? data?.seq ?? null,
      run_id: data?.run_id,
      thread_uid: data?.thread_uid,
      assistant_message_id: data?.assistant_message_id,
      thread_revision: data?.thread_revision,
      created_at: data?.created_at,
    };
    const payloadEventType = (data && typeof data === 'object' && typeof data.event === 'string')
      ? data.event.trim()
      : '';
    const logicalEventType = eventType && eventType !== 'message'
      ? eventType
      : (payloadEventType || eventType);
    if (logicalEventType === 'message.delta') {
      return { type: 'delta', text: data?.text || '', ...base };
    }
    if (logicalEventType === 'usage.update') {
      return {
        type: 'usage_update',
        usage: data?.usage,
        model: data?.model,
        provider_step: data?.provider_step,
        context_window: data?.context_window,
        context_meter: data?.context_meter,
        billing: data?.billing,
        ...base,
      };
    }
    if (['thinking.insight', 'thinking.progress', 'thinking.update', 'commentary'].includes(logicalEventType)) {
      return { type: 'thinking_insight', insight: normalizeThinkingInsight(logicalEventType === 'commentary' ? { ...data, mode: 'progress' } : data), ...base };
    }
    if (logicalEventType === 'context.handoff.started' || logicalEventType === 'context.handoff.completed') {
      return {
        type: 'context_handoff',
        phase: logicalEventType.endsWith('.started') ? 'started' : 'completed',
        data,
        ...base,
      };
    }
    if (logicalEventType === 'tool.call' || logicalEventType === 'tool.result') {
      return {
        type: logicalEventType === 'tool.call' ? 'tool_call' : 'tool_result',
        data: (typeof data === 'object' && data !== null) ? data : {},
        ...base,
      };
    }
    if (logicalEventType === 'message.completed') {
      return {
        type: 'completed',
        usage: data?.usage,
        model: data?.model,
        text: data?.text ?? data?.assistant_text ?? data?.output_text ?? '',
        raw_provider_messages: data?.raw_provider_messages,
        content_blocks: Array.isArray(data?.content_blocks)
          ? normalizeContentBlocks(data.content_blocks)
          : (Array.isArray(data?.assistant_content_blocks)
            ? normalizeContentBlocks(data.assistant_content_blocks)
            : (Array.isArray(data?.contentBlocks) ? normalizeContentBlocks(data.contentBlocks) : undefined)),
        thinking_insight: data?.thinking_insight ? normalizeThinkingInsight(data.thinking_insight) : undefined,
        service_tier: data?.service_tier,
        status: data?.status,
        reason_code: data?.reason_code,
        retry_count: data?.retry_count,
        partial_saved: data?.partial_saved,
        can_continue: data?.can_continue,
        notice: data?.notice,
        context_window: data?.context_window,
        context_meter: data?.context_meter,
        billing: data?.billing,
        openai_previous_response_id: data?.openai_previous_response_id,
        openai_response_ids: data?.openai_response_ids,
        openai_delta_items: data?.openai_delta_items,
        xai_previous_response_id: data?.xai_previous_response_id,
        xai_response_ids: data?.xai_response_ids,
        xai_delta_items: data?.xai_delta_items,
        ...base,
      };
    }
    if (logicalEventType === 'run.started') {
      return { type: 'run_started', data, ...base };
    }
    if (logicalEventType === 'run.cancelled') {
      return { type: 'cancelled', data, ...base };
    }
    if (logicalEventType === 'run.failed') {
      return { type: 'failed', data, ...base };
    }
    if (logicalEventType === 'error') {
      return { type: 'error', message: data?.message || 'Unknown error', data, ...base };
    }
    return { type: 'event', eventType: logicalEventType, data, ...base };
  };

  const findNextSeparator = (buf) => {
    const crlf = '\r\n\r\n';
    const lf = '\n\n';
    const iCRLF = buf.indexOf(crlf);
    const iLF = buf.indexOf(lf);
    if (iCRLF === -1 && iLF === -1) return { idx: -1, len: 0 };
    if (iCRLF === -1) return { idx: iLF, len: lf.length };
    if (iLF === -1) return { idx: iCRLF, len: crlf.length };
    return iCRLF < iLF ? { idx: iCRLF, len: crlf.length } : { idx: iLF, len: lf.length };
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    while (true) {
      const { idx, len } = findNextSeparator(buffer);
      if (idx < 0) break;
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + len);
      const parsed = parseEvent(rawEvent);
      if (parsed) yield parsed;
    }
  }
  const tail = buffer.trim();
  if (tail) {
    const parsed = parseEvent(tail);
    if (parsed) yield parsed;
  }
}
